/**
 * Reprise par connecteur depuis la console (ARTO-135, spec du 11 octobre 2026
 * § 10.2) : autoriser Dropbox pour un dossier d'ouverture, choisir le dossier
 * à copier, lancer l'import dans un dépôt, révoquer.
 *
 * Les routes sont sous `/v1/console/ouvertures/:dossierId/connecteurs/dropbox`
 * (`apps/api/src/console/reprise-connecteur.ts`). Le jeton d'accès n'est dans
 * aucune réponse : la console ne le voit jamais. En mode `?demo`, aucune requête
 * réseau (`demoConnecteurs.ts`).
 */
import { ApiError, modeDemo, requete } from './api.js';
import { demoConnecteurs } from './demoConnecteurs.js';
import type { NatureLot } from './reprise.js';

export type EtatConnexion = 'aucune' | 'en_attente' | 'autorisee' | 'revoquee' | 'purgee';
export type EtatImport =
  'en_attente' | 'en_cours' | 'termine' | 'plafond_atteint' | 'abandonne' | 'interrompu';

export interface ImportConnexion {
  etat: EtatImport;
  depotId: string;
  travailId: string;
  natures: NatureLot[];
  fichiers: number;
  octets: number;
  dejaPresents: number;
  ecartes: number;
  enregistrements: number;
  lanceLe: string;
  termineLe: string | null;
  /** Le code nommé de l'arrêt (`plafond_depasse`, `mandat_retire`…), sinon `null`. */
  raison: string | null;
}

export interface ConnexionDropbox {
  application: 'dropbox';
  /** Faux : la clé d'application manque sur la plateforme. */
  disponible: boolean;
  mode: 'simule' | 'reel' | null;
  etat: EtatConnexion;
  compte: string | null;
  autoriseeLe: string | null;
  autoriseePar: string | null;
  autorisationExpireLe: string | null;
  /** `null` : aucun dossier choisi ; `chemin` vide : tout le compte. */
  source: { chemin: string } | null;
  import: ImportConnexion | null;
  revoqueLe: string | null;
  purgeLe: string | null;
  revision: number;
}

export interface DossierDropbox {
  chemin: string;
  nom: string;
  parent: string | null;
}

export interface ListeDossiersDropbox {
  dossiers: DossierDropbox[];
  tronque: boolean;
}

export interface ReponseAutorisation {
  url: string;
  expireLe: string;
  connexion: ConnexionDropbox;
}

export interface ReponseLancement {
  depotId: string;
  travailId: string;
  /** Vrai : un import interrompu reprend dans son dépôt, au dernier curseur. */
  reprise: boolean;
  connexion: ConnexionDropbox;
}

/** Ce que Dropbox a répondu à la révocation du jeton. */
export type RevocationDistante = 'faite' | 'echec' | 'sans_objet';

export interface ReponseRevocation {
  revocationDistante: RevocationDistante;
  connexion: ConnexionDropbox;
}

export interface LancementImport {
  natures: NatureLot[];
  plafondOctets?: number;
}

const base = (dossierId: string): string =>
  `/v1/console/ouvertures/${encodeURIComponent(dossierId)}/connecteurs/dropbox`;

/** Un import tourne encore (ou attend son tour) : le suivi se relit. */
export function importEnCours(
  lancement: Pick<ImportConnexion, 'etat'> | null | undefined,
): boolean {
  return lancement?.etat === 'en_attente' || lancement?.etat === 'en_cours';
}

export const cleConnecteur = (dossierId: string): readonly unknown[] => [
  'console',
  'ouverture',
  dossierId,
  'connecteur',
  'dropbox',
];

export async function lireConnexion(dossierId: string): Promise<{ connexion: ConnexionDropbox }> {
  if (modeDemo()) return demoConnecteurs.lire(dossierId);
  return requete<{ connexion: ConnexionDropbox }>(base(dossierId));
}

export async function demanderAutorisation(dossierId: string): Promise<ReponseAutorisation> {
  if (modeDemo()) return demoConnecteurs.autoriser(dossierId);
  return requete<ReponseAutorisation>(`${base(dossierId)}/autorisation`, { methode: 'POST' });
}

export async function envoyerCode(
  dossierId: string,
  code: string,
): Promise<{ connexion: ConnexionDropbox }> {
  if (modeDemo()) return demoConnecteurs.coller(dossierId, code);
  return requete<{ connexion: ConnexionDropbox }>(`${base(dossierId)}/code`, {
    methode: 'POST',
    corps: { code },
  });
}

export async function listerDossiersDropbox(dossierId: string): Promise<ListeDossiersDropbox> {
  if (modeDemo()) return demoConnecteurs.dossiers(dossierId);
  return requete<ListeDossiersDropbox>(`${base(dossierId)}/dossiers`);
}

/** `chemin` vide : tout le compte. */
export async function choisirSource(
  dossierId: string,
  chemin: string,
): Promise<{ connexion: ConnexionDropbox }> {
  if (modeDemo()) return demoConnecteurs.choisir(dossierId, chemin);
  return requete<{ connexion: ConnexionDropbox }>(`${base(dossierId)}/source`, {
    methode: 'PUT',
    corps: { chemin },
  });
}

export async function lancerImport(
  dossierId: string,
  corps: LancementImport,
): Promise<ReponseLancement> {
  if (modeDemo()) return demoConnecteurs.lancer(dossierId, corps);
  return requete<ReponseLancement>(`${base(dossierId)}/lancer`, { methode: 'POST', corps });
}

export async function revoquerConnexion(dossierId: string): Promise<ReponseRevocation> {
  if (modeDemo()) return demoConnecteurs.revoquer(dossierId);
  return requete<ReponseRevocation>(base(dossierId), { methode: 'DELETE' });
}

// ── Erreurs nommées ──────────────────────────────────────────────────────

/** Les raisons de l'API que l'écran dit en clair (`details.raison`). */
export const RAISONS_CONNECTEUR = [
  'connecteur_indisponible',
  'galerie_absente',
  'deja_autorisee',
  'code_refuse',
  'autorisation_expiree',
  'autorisation_concurrente',
  'autorisation_retiree',
  'dossier_inconnu',
  'import_en_cours',
  'source_absente',
  'connexion_non_autorisee',
  'connexion_modifiee',
  'jeton_illisible',
  'reprise_close',
] as const;
export type RaisonConnecteur = (typeof RAISONS_CONNECTEUR)[number];

export function raisonConnecteur(erreur: unknown): RaisonConnecteur | null {
  if (!(erreur instanceof ApiError)) return null;
  const details = erreur.details as { raison?: unknown } | null;
  return RAISONS_CONNECTEUR.find((raison) => raison === details?.raison) ?? null;
}

/** Dropbox n'a pas répondu ou a refusé un appel (`connecteur.echec`). */
export function estEchecConnecteur(erreur: unknown): boolean {
  return erreur instanceof ApiError && erreur.code === 'connecteur.echec';
}
